<template>
  <VDialogV3
    id="demand-card"
    position="right"
    :width="dialogWidth"
    :entity="localDemand"
    :loading="localDemand === null"
    :context-menu="isBuyer ? DemandsService.contextMenu : null"
    :tabs="tabs"
    :active-tab="activeTab"
    @update:active-tab="onUpdateActiveTab"
    @submit="onSubmit"
  >
    <template #header>
      <div
        class="title"
        :title="localDemand?.productName"
      >
        Потребность "{{ localDemand?.productName }}"
      </div>
    </template>

    <section
      v-if="activeTab.id === 'main'"
    >
      <VInput
        layout="vertical"
        label="Наименование"
        disabled
        :value="localDemand.productName"
      />

      <VInput
        layout="vertical"
        label="Артикул товара"
        disabled
        :value="localDemand.product.code"
      >
        <template #helper-text v-if="localDemand.hasTender">
          Тендер:
          <VLink @click="TendersService.editTender(localDemand.tender.id)">
            {{ localDemand.tender.id }}
          </VLink>
        </template>
      </VInput>

      <VCheckboxGroup
        v-model="activeOrderType"
        label="Тип заказа"
        layout="vertical"
        type-radio
        checkbox-layout="horizontal"
        option-label="name"
        disabled
        :options="ordersType"
      />

      <VInputNumberRusV2
        v-model="localDemand.qtyDesired"
        :type="Qty"
        label="Количество"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :unit="localDemand.unit?.name"
      />

      <VDateInput
        v-if="!isPreorder"
        v-model="localDemand.desiredReceiptDate"
        label="Дата поставки"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :validate-method="validateDate"
      />

      <VInputNumberRusV2
        v-if="isPreorder"
        v-model="localDemand.preorderMonthlyReserve"
        :type="Qty"
        label="Страховой запас"
        layout="vertical"
        :disabled="!isBuyer"
        :unit="localDemand.unit?.name"
      />

      <VDateRangeInputNew
        v-if="isPreorder"
        v-model="localDemand.preorderShipmentPeriod"
        label="Дата поставки"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :validate-method="validateDate"
      />

      <VSelect
        v-if="isBuyer"
        v-model="localDemand.responsibleBuyer"
        label="Ответственный"
        placeholder="Введите ФИО ответственного"
        layout="vertical"
        required
        :disabled="!isBuyer"
        :search-function="pattern => PersonsService.searchPersons(pattern, $core.currentPartner.id)"
      />
    </section>

    <template
      v-if="activeTab.id === 'supplyOffers'"
    >
      <SupplyOffersRegistry
        :product="localDemand.product"
        class="fill"
      />
    </template>


    <ProductCharacteristicsReadModeBlock
      v-if="localDemand?.product && activeTab.id === 'characteristic'"
      :product="localDemand.product"
      hide-edit
    />

    <section v-if="activeTab.id === 'buyer'">
      <VInput
        label="Покупатель"
        layout="vertical"
        disabled
        :value="localDemand.buyer.name"
      />

      <VSelect
        v-model="localDemand.buyerCompany"
        label="Юр. лицо"
        layout="vertical"
        :disabled="!isBuyer"
        :options="localDemand.buyer.companies"
      />

      <VSelect
        v-model="localDemand.buyerStore"
        label="Склад"
        layout="vertical"
        :disabled="!isBuyer"
        :options="localDemand.buyer.stores ?? [localDemand.buyerStore]"
      />

      <VSelect
        v-if="localDemand.buyerProductionSite"
        v-model="localDemand.buyerProductionSite"
        label="Производственная площадка"
        layout="vertical"
        :disabled="!isBuyer"
        :options="localDemand.buyerCompany.productionSites"
      />
    </section>

    <FilesList
      v-if="activeTab.id === 'files'"
      :entities="fileEntities"
    />
  </VDialogV3>
</template>

<script lang="ts">

import DateSimple from '@/core/types/DateSimple';
import DemandsService from '@/domains/Demand/DemandsService';
import PersonsService from '@/domains/Persons/PersonsService';
import EntityPointer from '@/domains/EntityPointer/EntityPointer';
import TendersService from "@/domains/Tenders/TendersService";
import Demand from '@/domains/Demand/Demand';
import Qty from '@/core/types/Qty';

import ProductCharacteristicsReadModeBlock
  from '@/domains/Products/components/ProductCharacteristicsReadModeBlock/ProductCharacteristicsReadModeBlock.vue';
import SupplyOffersRegistry
  from '@/domains/SupplyOffers/components/SupplyOffersRegistry/SupplyOffersRegistry.vue';

export default defineComponent({
  name: 'DemandCard',

  components: {
    SupplyOffersRegistry,
    ProductCharacteristicsReadModeBlock,
  },

  props: {
    id: {
      type: Number,
      default: null,
    },
  },

  emits: ['close', Demand.Events.Updated],

  setup() {
    return {
      DemandsService,
      PersonsService,
      TendersService,
      /** Класс нужен шаблону для :type у VInputNumberRusV2 */
      Qty,
    };
  },

  data() {
    return {
      localDemand: null as Demand | null,

      ordersType: [
        {id: 'byOrder', name: 'Обычный заказ'},
        {id: 'byPreorder', name: 'По предзаказу'},
      ],
      activeOrderType: null as { id: string; name: string } | null,

      activeTab: undefined,
    };
  },

  computed: {
    /** Текущий пользователь — покупатель */
    isBuyer(): boolean {
      return this.$core.currentPartner?.isBuyer === true;
    },

    /** Текущий пользователь — поставщик */
    isSeller(): boolean {
      return this.$core.currentPartner?.isSeller === true;
    },

    /** Табы карточки. Таб поставщиков доступен только покупателю, таб файлов — тоже. */
    tabs() {
      const baseTabs = [
        {id: 'main', name: 'Основное'},
        {id: 'supplyOffers', name: 'Поставщики'},
        {id: 'characteristic', name: 'Характеристики'},
        {id: 'buyer', name: 'Покупатель'},
      ];

      if (this.isBuyer) {
        baseTabs.push({id: 'files', name: 'Файлы'});
      }

      if (this.isSeller) {
        return baseTabs.filter(tab => tab.id !== 'supplyOffers');
      } else {
        return baseTabs;
      }
    },

    isPreorder(): boolean {
      return this.localDemand?.isPreorderNeeded === true;
    },

    fileEntities() {
      return [new EntityPointer('demand', this.localDemand!.id, this.localDemand!.toString())];
    },

    /** На вкладке разрешений таблица широкая, остальным вкладкам лишнее место ни к чему */
    dialogWidth(): string {
      if (this.activeTab?.id === 'supplyOffers') {
        return '1210px';
      }
      else {
        return '605px';
      }
    },
  },

  async mounted() {
    await this.init();
  },

  methods: {
    /** Загружает потребность: id или число в demand → get, объект → клон. */
    async init(): Promise<void> {
      if (this.id !== null) {
        this.localDemand = await DemandsService.get(this.id!);
      }
      // TODO else -> new??

      this.activeOrderType = this.ordersType.find(type => (this.localDemand!.isPreorderNeeded
        ? type.id === 'byPreorder'
        : type.id === 'byOrder')) ?? null;
    },

    /** Смена таба. При открытии таба покупателя подгружает производственные площадки. */
    onUpdateActiveTab(tab: string): void {
      this.activeTab = tab;

      if (tab === 'buyer' && this.localDemand?.buyerCompany.hasProductionSites() === false) {
        this.localDemand.buyerCompany.loadProductionSites();
      }
    },

    /** Сохраняет потребность. Вызывается из @submit (VDialogV3 уже провалидировал форму). */
    async onSubmit(): Promise<void> {
      const success = await DemandsService.update(this.localDemand!);

      if (success) {
        this.$emit(Demand.Events.Updated);
        this.$core.dialogs.DemandCard.resolve();
      }
    },

    /** Запрещает прошедшую дату поставки. */
    validateDate(date: any): string | null {
      if (this.$core.utils.date.isDate(date) === false) return null;

      /** Календарь зовёт валидатор для каждой ячейки дня и передаёт нативный
       *  Date — isBefore и equals объявлены на DateSimple, на нативной дате
       *  их нет. Приводим тип, аргументом обе функции принимают и Date. */
      const dateSimple = new DateSimple(date);
      const today = DateSimple.today();

      if (dateSimple.isBefore(today) || dateSimple.equals(today)) {
        return 'Нельзя указать прошедшую дату';
      }

      return null;
    },
  },
});
</script>
